<template>
  <div
    class="wrapper-data-table"
    :class="[
      dataTableClass,
      {
        'sticky-header': stickyHeader,
        'moveable-row': moveRows,
      },
    ]"
  />
</template>

<script>
import Tabulator from "tabulator-tables";
import table from "@/mixins/table";
import moment from "moment";

export default {
  mixins: [table],
  props: {
    cellEditedCb: {
      required: false,
      default: () => {},
      type: Function,
    },
    editable: {
      required: false,
      default: false,
      type: Boolean,
    },
    isReactive: {
      require: false,
      default: false,
      type: Boolean,
    },
    moveRows: {
      required: false,
      default: false,
      type: Boolean,
    },
    stickyHeader: {
      required: false,
      default: false,
      type: Boolean,
    },
    pagination: {
      required: false,
      default: false,
      type: Boolean,
    },
    selectable: {
      required: false,
      default: false,
      type: Boolean,
    },
    maxHeightTable: {
      type: String,
      default: "fit-content",
      required: false,
    },
    filePrefix: {
      type: String,
      default: "relatorio",
      required: false,
    },
    leftFrozenColumns: {
      required: false,
      default: () => [],
      type: Array,
    },
    staticColumns: {
      required: false,
      default: () => [],
      type: Array,
    },
    tableData: {
      required: false,
      default: () => [],
      type: Array,
    },
  },
  data() {
    return {
      tabulator: null,
      currentCellEditing: null,
      selectableColumn: {
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        headerSort: false,
        minWidth: 50,
        maxWidth: 50,
        download: false,
      },
      moveableColumn: {
        rowHandle: true,
        formatter: "handle",
        headerSort: false,
        frozen: true,
        width: 40,
        minWidth: 40,
        // minWidth: 70,
        // maxWidth: 70,
        download: false,
      },
      paginationConfigs: {
        pagination: "local",
        paginationSize: 10,
        paginationSizeSelector: [5, 10, 20, 30, 50, 100, 200],
        movableColumns: true,
        paginationCounter: "rows",
        locale: true,
        langs: {
          "pt-br": {
            //French language definition
            pagination: {
              first: "Primeira",
              first_title: "Primeira página",
              last: "Última",
              last_title: "Última página",
              prev: "Anterior",
              prev_title: "Página anterior",
              next: "Próxima",
              next_title: "Próxima página",
              page_size: "Items por página",
              all: "Todas",
            },
          },
        },
      },
      defaultColConfig: {
        hozAlign: "center",
        headerHozAlign: "center",
        headerSort: false,
        minWidth: 130,
        accessorDownload: this.decimalFormatter,
      },
    };
  },
  watch: {
    itemsSelected: {
      handler(val) {
        this.$emit("update-selected-items", val);
      },
    },
    tableData: {
      handler(val) {
        this.tabulator.replaceData(val);
        this.tabulator.redraw();
      },
    },
    columns: {
      handler(val) {
        this.tabulator.setColumns(val);
        this.tabulator.redraw();
      },
      deep: true,
    },
    // selectedItems:{
    //   handler(val) {
    //     console.log('Mudando seleted items >>> ',this.selectedItems)
    //     if(this.selectedItems.length){
    //       const users= this.selectedItems.map(user=>user.NAME)
    //       this.tabulator.setFilter("EDITOR_NAME", "in", users);
    //     }else{
    //       this.tabulator.clearFilter();
    //     }
    //   },
    //   deep: true,
    // }
  },
  computed: {
    tabulatorConfigs() {
      return {
        // dataEdited: function (data) {
        //   console.log('DATA CHANGED - ',{data});
        // },
        rowMoved: this.moveRow,
        data: this.tableData,
        maxHeight: this.maxHeightTable,
        reactiveData: this.isReactive,
        history: false,
        movableColumns: false,
        resizableColumns: false,
        resizableRows: false,
        movableRows: this.moveRows ? true : false,
        layout: "fitColumns",
        columns: this.columns,
      };
    },
    fileName() {
      return `${this.filePrefix}_${new Date().toJSON().slice(0, 10)}`
        .toLowerCase()
        .split(" ")
        .join("-");
    },
    dataTableClass() {
      return `data-table-random-class-${this._uid}`;
    },
    columns() {
      let leftFrozenColumns = [];

      if (this.moveRows) {
        leftFrozenColumns.push(this.moveableColumn);
      }

      if (this.selectable) {
        leftFrozenColumns.push(this.selectableColumn);
      }

      leftFrozenColumns.push(...this.leftFrozenColumns);

      leftFrozenColumns = leftFrozenColumns.map((col) => {
        col = {
          editable: false,
          frozen: true,
          ...col,
        };

        return col;
      });

      const staticColumns = this.staticColumns.map((col) => {
        col = {
          editor: "input",
          editable: () => (this.editable ? true : false),
          cellEditing: this.editingCell,
          cellEditCancelled: function (cell) {
            cell.setValue(cell.getOldValue(), true);
          },
          ...col,
        };

        return col;
      });

      return [...leftFrozenColumns, ...staticColumns].map((col) => {
        col = {
          ...this.defaultColConfig,
          ...col,
        };

        return col;
      });
    },
    itemsSelected() {
      return this.tabulator ? this.tabulator.getSelectedData() : [];
    },
  },
  methods: {
    editingCell(cell) {
      this.currentCellEditing = cell;
      window.addEventListener("keyup", this.addKeyListener);
    },
    addKeyListener(e) {
      const goNextCell = e.keyCode === 13;
      if (goNextCell) {
        this.currentCellEditing.nav().next();
      }
    },
    removeKeyListener() {
      window.removeEventListener("keyup", this.addKeyListener);
    },

    importJSON() {
      this.tabulator
        .setDataFromLocalFile()
        .then(() => this.updateTableData(this.tabulator.getData()));
    },

    moveRow(row) {
      const updatedTableData = row
        .getTable()
        .getData()
        .map((rowData, index) => {
          rowData.DAP = index;
          return rowData;
        });

      this.updateTableData(updatedTableData);
    },

    updateTableData(data) {
      this.$emit("update-table-data", data);
    },

    downloadXLSX() {
      this.tabulator.download("xlsx", `${this.fileName}.xlsx`, {
        sheetName: new Date().getTime().toString(),
      });
    },

    downloadCSV() {
      this.tabulator.download("csv", `${this.fileName}.csv`), { bom: true };
    },

    getShadowTabulator(params = {}) {
      let hiddenTabulator = document.querySelector("#hidden-tabulator");

      if (hiddenTabulator) {
        hiddenTabulator.outerHTML = "";
      }

      hiddenTabulator = document.createElement("div");

      hiddenTabulator.style.display = "none";
      hiddenTabulator.setAttribute("id", "hidden-tabulator");

      const body = document.querySelector("body");
      body.appendChild(hiddenTabulator);

      let rawColumns = this.columns.map((col) => {
        const copyCol = { ...col };

        if (!params.masks) {
          delete copyCol.accessorDownload;
        }

        delete copyCol.frozen;

        return copyCol;
      });

      const shadowTabulator = new Tabulator("#hidden-tabulator", {
        ...this.tabulatorConfigs,
        columns: rawColumns,
      });

      return shadowTabulator;
    },

    destroyShadowTabulator() {
      const hiddenTabulator = document.querySelector("#hidden-tabulator");
      const body = document.querySelector("body");

      body.removeChild(hiddenTabulator);
    },

    downloadJSON() {
      this.getShadowTabulator().download("json", `${this.fileName}.json`);

      this.destroyShadowTabulator();
    },

    downloadPDF() {
      const filePrefix = this.filePrefix;
      const columns = this.columns;

      this.getShadowTabulator({ masks: true }).download(
        "pdf",
        `${this.fileName}.pdf`,
        {
          documentProcessing: function (workbook) {
            workbook.Props = {
              Title: "SheetJS Tutorial",
              Subject: "Test",
              CreatedDate: new Date(),
            };

            return workbook;
          },

          autoTable: function (doc) {
            const pageWidth =
              doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

            doc.setFontSize(10);

            doc.text(filePrefix, 40, 20);

            doc.text(moment().format("DD/MM/YYYY"), pageWidth - 90, 20, {
              styles: { fontSize: 10 },
            });

            return {
              headStyles: {
                fillColor: [123, 179, 75],
                cellWidth: 5,
                overflow: "linebreak",
                overflowColumns: "linebreak",
                bodyStyles: { valign: "top" },
                rowPageBreak: "wrap",
                halign: "center",
                valign: "middle",
              },
              styles: {
                fontSize: 10,
                cellWidth: "wrap",
                halign: "center",
              },
              alternateRowStyles: {
                fillColor: [240, 244, 244],
              },
            };
          },
        }
      );

      this.destroyShadowTabulator();
    },

    extractRowValuesOrdered(rowObj) {
      const headersKeys = this.columns
        .filter((col) => !col.frozen)
        .map((col) => col.field);

      let rowValues = [];

      headersKeys.forEach((key) => rowValues.push(rowObj[key]));

      return rowValues;
    },
  },
  mounted() {
    //instantiate Tabulator when element is mounted
    this.tabulator = new Tabulator(
      `.wrapper-data-table.${this.dataTableClass}`,
      {
        ...this.tabulatorConfigs,
        ...(this.pagination ? this.paginationConfigs : ""),
      }
    );

    this.tabulator.setLocale("pt-br");
  },
};
</script>
<style scoped>
.wrapper-data-table {
  height: fit-content;
  width: 100%;
  overflow: auto;
}

.wrapper-data-table.tabulator >>> .table-option {
  position: sticky;
  top: 0;
  z-index: 999;
  padding-bottom: 80px;
  background: white;
  margin: 0;
}

.wrapper-data-table >>> .tabulator-col-sorter > .tabulator-arrow {
  top: 50% !important;
  transform: translateY(-50%);
  border-bottom-color: #3a3535 !important;
  border-top-color: #3a3535 !important;
}

.wrapper-data-table >>> .tabulator-col-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.wrapper-data-table >>> .tabulator-col {
  height: 3em !important;
}

.wrapper-data-table >>> .tabulator-cell {
  cursor: auto !important;
}

.tabulator {
  font-family: "Source Sans Pro", sans-serif;
  font-size: 13px;
  border: none;
}
.wrapper-data-table >>> .tabulator-header {
  font-weight: normal;
  border: 1px solid rgb(170, 170, 170);
  color: rgba(0, 0, 0, 0.54);
}

.wrapper-data-table.sticky-header >>> .tabulator-header {
  position: sticky;
  top: 0;
  z-index: 100;
}
.wrapper-data-table >>> .tabulator-header .tabulator-col {
  height: 38px;
  background-color: #f7f7f7;
  padding: 0 6px;
  font-size: 13px;
  text-align: center;
  font-weight: 600;
  color: #050505;
}
/* .wrapper-data-table >>> .tabulator-row.tabulator-row-even {
  background-color: #fcfcfc;
} */

.wrapper-data-table >>> .tabulator-sortable:hover {
  background-color: #e6e6e6 !important;
}
.wrapper-data-table >>> .tabulator-tableHolder {
  border: 1px solid rgb(170, 170, 170);
  border-top: none;
  /* max-height: 40vh !important; */
}

.wrapper-data-table >>> .tabulator-col-title {
  width: 100%;
  max-width: 100%;
  word-break: break-word !important;
  white-space: initial !important;
  /*
  overflow: hidden !important;
  text-overflow: ellipsis !important;

  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
          line-clamp: 2 !important;
 -webkit-box-orient: vertical !important;
  -webkit-box-3orient: horizontal !important;

  display: invalid-atribute; */

  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 16px;
  max-height: 32px;
}

.wrapper-data-table.moveable-row >>> .tabulator-row-handle {
  cursor: grab !important;
}

/* @media screen and (max-width: 1400px) {
  .tabulator {
    height: 85%;
  }
}
@media screen and (min-width: 1400px) {
  .tabulator {
    height: 95%;
  }
} */
</style>
